import axios from 'axios';

/**
 * 
 * @param {string} clientId - the id of the client (mongo object id from the 'clients' collection).
 * @param {string[]} siteIds - an array of strings of siteIds
 * @param {*} signal - the cancel signal sent to Axios (default = null)
 * @returns array of users
 */
const getUsersForSites = (clientId, siteIds, signal = null) => {
  
  return new Promise((resolve, reject) => {
    if (!siteIds || siteIds.length === 0) {
      return reject(new Error('Invalid user siteIds array'));
    }
    axios.get(`/v1/users?sites=${siteIds}&client=${clientId}`, { signal })
    .then(response => resolve(response.data))
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      reject(err)
    });
  });
};

const getConcordeUsers = (signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get('/v1/users?concordeUsers=true', {signal})
    .then(response => resolve(response.data))
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      reject(err)
    });
  });
};

// todo: should be getUserFavoriteCharts
const getFavoriteCharts = (userId, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/users/${userId}`, {signal})
    .then(res => {
      if (res.status !== 200) {
        return reject(new Error('Unexpected response from server'));
      }
      resolve(res.data.favoriteCharts);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      reject(err);
    })
  });
};

const saveUserFavoriteChart = (userId, chartSignature, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.post(`/v1/users/${userId}/favoritecharts`, chartSignature, { signal})
    .then(res => {
      if (res.status !== 200) {
        return reject(new Error("Unexpected response from server"));
      }
      resolve(res.data);
    })
    .catch(err => reject(err))
  });
};

const deleteUserFavoriteChart = (userId, chartId, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.delete(`/v1/users/${userId}/favoritecharts/${chartId}`, { signal })
    .then(res => {
      if (res.status !== 201) {
        return reject(new Error("Unexpected response from server"));
      }
      resolve();
    })
    .catch(err => reject(err))
  });
};

const getUser = (userId, signal = null) => {
  return new Promise((resolve, reject) => {
    if (!userId) {
      return reject(new Error('User Id is required'));
    }
    axios.get(`/v1/users/${userId}`, { signal })
    .then(res => {
      if (res.status !== 200) {
        return reject(new Error('Unexpected response from server'));
      }
      resolve(res.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      reject(err);
    });
  });
};

const getUsersByEmail = (email, signal = null) => {
  return new Promise((resolve, reject) => {
    if (!email) {
      return reject(new Error('Email address is required'));
    }
    axios.get(`/v1/users?email=${encodeURI(email)}`, {signal})
    .then(res => {
      if (res.status !== 200) {
        return reject(new Error('Unexpected response from server'));
      }
      resolve(res.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      reject(err);
    });
  });
};

const getCCRepLinkedToUser = (userId, signal = null) => {
  return new Promise((resolve, reject) => {
    if (!userId) {
      return reject(new Error('User Id is required'));
    }
    axios.get(`/v1/users/${userId}/ccrep`, { signal })
    .then(res => {
      resolve(res.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      reject(err);
    });
  });
};

/**
 * Get client child locations that a user has access to
 * @param {string} clientId - The ID of the client
 * @param {string} userId - The ID of the user
 * @return {Promise<Array>} - A promise that resolves to an array of DOT numbers
 * @throws {Error} - Throws an error if clientId or userId is not provided
 */
const getClientChildrenForUser = async (clientId, userId) => {
  if (!clientId || !userId) {
    throw new Error('clientId and userId are required');
  }
  try {
    const dotList = await axios.get(`/v1/clients/${clientId}/children?source=legacy&includeParent=true&forUser=${userId}`);
    return dotList.data;
  } catch (error) {
    console.error('Error fetching DOT numbers:', error);
    throw error;
  }
};

/**
 * Fetch DOT and Lab Account data for a given client and user.
 * @param {string} clientId - The legacy client ID.
 * @param {string} userId - The current user object ID.
 * @param {boolean} panels - Whether to include panel info (optional).
 * @returns {Promise<Object>} - { dotResults, labAccounts }
 */
const getDotAndLabData = async (clientId, userId, panels = false) => {
  try {
    const response = await axios.get(`/v1/clients/${clientId}/users/${userId}/dotLabAccounts`, {
      params: {
        panels
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching DOT and lab account info:', error);
    throw error;
  }
};

export default {
  getUser,
  getUsersByEmail,
  getCCRepLinkedToUser,
  getUsersForSites,
  getConcordeUsers,
  getFavoriteCharts,
  deleteUserFavoriteChart,
  saveUserFavoriteChart,
  getClientChildrenForUser,
  getDotAndLabData
};